import type { Middleware } from '@floating-ui/react-dom';
import { autoUpdate, useFloating } from '@floating-ui/react-dom';
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import React, { useLayoutEffect, useRef } from 'react';
import ReactionAvatarStack, {
  ReactionAvatarStackItemContainer,
} from './ReactionAvatarStack';
import * as styles from './ThreadReactionList.module.scss';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import PopOver from '../../components/deprecated/PopOver';
import EmojiPicker, {
  EmojiPickerContainer,
} from '../../components/EmojiPicker';
import SpringAnimation from '../../components/SpringAnimation';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import caretIcon from '../../icons/non-prefilled/16x16/caret.svg';
import { UIActions } from '../../ui/uiSlice';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import type { ExtractReactionThreadTypes } from '../../utils/post/threadUtils';
import { getScrollContainer } from '../listenToPostScroll';

const REACTIONS_FRAGMENT = gql(`
  fragment ThreadReactionList_reactions on ReactionFields {
    id
    ...ReactionAvatarStack_reaction
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment ThreadReactionList_thread on RootThreadFields {
    id
    ... on ReactionThread {
      reactions {
        id
        ...ThreadReactionList_reactions
      }
    }
    ... on PublicReactionThread {
      reactions {
        id
        ...ThreadReactionList_reactions
      }
    }
  }
`);

export const OPTIMISTIC_REACTION_IDS = new Set<string>();

const SCREEN_PADDING = 8;

const positionPicker = (options: { shiftTop: number }): Middleware => ({
  name: 'positionPicker',
  options,
  fn(state) {
    const floatingRect = state.elements.floating.getBoundingClientRect();
    const referenceRect = state.elements.reference.getBoundingClientRect();

    const emojiPickerLeft = referenceRect.right - floatingRect.width;

    const emojiPickerHalfWidth = floatingRect.width / 2;
    const halfWidth = referenceRect.width / 2;

    const diff =
      referenceRect.left + halfWidth - (emojiPickerLeft + emojiPickerHalfWidth);

    const boundaryElement = getScrollContainer() ?? document.body;

    // This excludes the scrollbar
    const boundaryRight =
      boundaryElement.getBoundingClientRect().left +
      boundaryElement.clientWidth;

    const correctionRight = Math.max(
      0,
      emojiPickerLeft +
        diff +
        floatingRect.width -
        (boundaryRight - SCREEN_PADDING),
    );

    const correctionLeft = Math.max(
      0,
      boundaryElement.getBoundingClientRect().left +
        SCREEN_PADDING -
        (emojiPickerLeft + diff),
    );

    const finalDiff = diff - correctionRight + correctionLeft;

    return {
      x: state.x + finalDiff,
      y: state.y + options.shiftTop,
    };
  },
});

interface PickerPopOverProps {
  reactions: DocumentType<typeof REACTIONS_FRAGMENT>[];
  onAdd: (emoji: string) => void;
  onRemove?: (id: string) => void;
  onOpenChange: (isOpen: boolean) => void;
  containerRef: React.RefObject<HTMLElement>;
  open: boolean;
  lastClickedRef: React.MutableRefObject<EventTarget | null>;
}

const middleware = [positionPicker({ shiftTop: 5 })];

const PickerPopOver = ({
  reactions,
  onAdd,
  onRemove,
  onOpenChange,
  containerRef,
  open,
  lastClickedRef,
}: PickerPopOverProps) => {
  const currentUser = useAppSelector((state) => state.session.user);

  const { refs, floatingStyles, isPositioned } = useFloating({
    middleware,
    whileElementsMounted: autoUpdate,
    open,
    placement: 'bottom-end',
  });

  useLayoutEffect(() => {
    refs.setReference(containerRef.current);
  }, [containerRef, refs, open]);

  if (!open) return null;

  const selectedEmojis = new Set(
    reactions
      .filter((reaction) => reaction.author.id === currentUser.id)
      .map((reaction) => reaction.emoji),
  );

  return (
    <div
      ref={refs.setFloating}
      // we need to right align it before it's positioned because otherwise, it would shift the
      // main content to the left as it's overflowing the magnetic wall. Once it's positioned,
      // we can pass the calculated floating styles.
      style={
        open && isPositioned
          ? floatingStyles
          : { position: 'absolute', right: 0 }
      }
    >
      <AnimatePresence>
        {open && (
          <SpringAnimation direction="down">
            <ClickOutsideHandler
              onClickOutside={(e) => {
                lastClickedRef.current = e.target;
                onOpenChange(false);
              }}
            >
              <PopOver.Container noPadding width="auto">
                <EmojiPickerContainer>
                  <EmojiPicker
                    inline
                    selectedEmojis={selectedEmojis}
                    onRemove={(emoji: string) => {
                      const reaction = reactions.find(
                        (r) =>
                          r.author.id === currentUser.id && r.emoji === emoji,
                      );
                      if (reaction) {
                        onRemove?.(reaction.id);
                      }
                    }}
                    onPick={(emoji) => {
                      onAdd(emoji);
                      onOpenChange(false);
                    }}
                    initialEmojisTimeout={500}
                  />
                </EmojiPickerContainer>
              </PopOver.Container>
            </ClickOutsideHandler>
          </SpringAnimation>
        )}
      </AnimatePresence>
    </div>
  );
};

interface ThreadReactionListProps {
  expanded: boolean;
  thread: ExtractReactionThreadTypes<typeof THREAD_FRAGMENT>;
  withinOverlay?: boolean;
  isEmojiPickerOpen?: boolean;
  onEmojiPickerOpenChange?: React.Dispatch<React.SetStateAction<boolean>>;
  maxItemsShown?: number;
  spacing?: React.ComponentProps<typeof ReactionAvatarStack>['spacing'];
  onContainerClick?: () => void;
  showAdditionalNode: boolean;
  hideOverflowCount: boolean;
  onAdditionalNodeClick: () => void;
  addReaction: (emoji: string) => void;
  deleteReaction: (id: string) => void;
}

const ThreadReactionList = ({
  expanded,
  thread,
  withinOverlay,
  isEmojiPickerOpen = false,
  onEmojiPickerOpenChange,
  maxItemsShown,
  spacing,
  onContainerClick,
  showAdditionalNode,
  hideOverflowCount,
  onAdditionalNodeClick,
  addReaction,
  deleteReaction,
}: ThreadReactionListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastClickedRef = useRef<EventTarget | null>(null);
  const currentUser = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();

  const handleAddReaction = (emoji: string) => {
    dispatch(UIActions.addToRecentlyUsedEmojis(emoji));
    addReaction(emoji);
  };

  const currentUserEmojis = thread.reactions
    .filter((reaction) => reaction.author.id === currentUser.id)
    .map((reaction) => reaction.emoji);

  return (
    <div
      className={classNames(styles.container, {
        [styles.expanded]: expanded,
      })}
      ref={containerRef}
      onClick={onContainerClick}
    >
      <ReactionAvatarStack
        reactions={thread.reactions}
        maxItemsShown={maxItemsShown}
        expanded={expanded}
        onEmojiClick={
          expanded
            ? (reaction, event) => {
                if (event.target === lastClickedRef.current) {
                  lastClickedRef.current = null;
                  return;
                }

                lastClickedRef.current = event.target;
                if (
                  reaction.author.id === currentUser.id &&
                  currentUserEmojis.includes(reaction.emoji)
                ) {
                  deleteReaction(reaction.id);
                } else if (!currentUserEmojis.includes(reaction.emoji)) {
                  handleAddReaction(reaction.emoji);
                }
              }
            : undefined
        }
        withinOverlay={withinOverlay}
        spacing={spacing}
        showAdditionalNode={showAdditionalNode}
        hideOverflowCount={hideOverflowCount}
        additionalNode={
          <Tooltip content="Add reaction">
            <Button
              onClick={(event) => {
                if (event.target === lastClickedRef.current) {
                  lastClickedRef.current = null;
                  return;
                }
                onAdditionalNodeClick();
              }}
            >
              <ReactionAvatarStackItemContainer
                className={styles.reactionCaretButtonContainer}
              >
                <span
                  className={classNames(styles.icon, {
                    [styles.isOpen]: isEmojiPickerOpen,
                  })}
                >
                  <SVGIcon size={14}>{caretIcon}</SVGIcon>
                </span>
              </ReactionAvatarStackItemContainer>
            </Button>
          </Tooltip>
        }
      />
      {onEmojiPickerOpenChange && (
        <PickerPopOver
          reactions={thread.reactions}
          onAdd={handleAddReaction}
          onRemove={deleteReaction}
          onOpenChange={onEmojiPickerOpenChange}
          containerRef={containerRef}
          open={isEmojiPickerOpen}
          lastClickedRef={lastClickedRef}
        />
      )}
    </div>
  );
};

export default ThreadReactionList;
